import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import { DateFormatPattern, DiagramCalendar, HolidayCountry } from '../api/types';
import { normalizeDateToken, formatDate } from '../utils/dateFormat';
import { useAuth } from '../contexts/AuthContext';

/** Lundi en premier (convention française) — valeurs 0=dimanche..6=samedi comme en base. */
const WEEKDAYS: { value: number; labelKey: string }[] = [
  { value: 1, labelKey: 'calendar.weekday.mon' },
  { value: 2, labelKey: 'calendar.weekday.tue' },
  { value: 3, labelKey: 'calendar.weekday.wed' },
  { value: 4, labelKey: 'calendar.weekday.thu' },
  { value: 5, labelKey: 'calendar.weekday.fri' },
  { value: 6, labelKey: 'calendar.weekday.sat' },
  { value: 0, labelKey: 'calendar.weekday.sun' },
];

/** Texte libre (dans le format préféré de l'utilisateur) -> liste ISO déduplicée et triée chronologiquement. */
function parseDatesText(text: string, format: DateFormatPattern): string[] {
  // Un token non reconnu (normalizeDateToken renvoie null) est laissé tel
  // quel plutôt qu'avalé silencieusement : la validation du serveur
  // (IsDateString) le signalera clairement à l'enregistrement.
  return [...new Set(text.split(',').map((s) => normalizeDateToken(s, format) ?? s.trim()).filter(Boolean))].sort();
}

/** Liste ISO -> texte affiché dans le format préféré de l'utilisateur, triée chronologiquement (donc AVANT reformatage : un tri lexicographique sur du JJ-MM-AAAA ou MM-JJ-AAAA ne serait pas chronologique). */
function isoListToDisplayText(isoDates: string[], format: DateFormatPattern): string {
  return [...new Set(isoDates)]
    .sort()
    .map((iso) => formatDate(iso, format))
    .join(', ');
}

export function CalendarSettingsModal({
  diagramId,
  canWrite,
  onClose,
}: {
  diagramId: string;
  canWrite: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const dateFormat = user?.dateFormat ?? 'DD-MM-YYYY';
  const [workingDays, setWorkingDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [datesText, setDatesText] = useState('');
  const [country, setCountry] = useState<HolidayCountry>('FR');
  const [year, setYear] = useState(new Date().getFullYear());
  const [pickedDate, setPickedDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get<DiagramCalendar>(`/diagrams/${diagramId}/calendar`)
      .then((cal) => {
        setWorkingDays(cal.workingDays);
        setDatesText(isoListToDisplayText(cal.nonWorkingDates.map((d) => d.slice(0, 10)), dateFormat));
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
    // dateFormat n'est utilisé qu'au chargement initial : réagir à un changement de préférence pendant que la modale est déjà
    // ouverte reformatterait le texte sous les yeux de l'utilisateur ET écraserait ses éventuelles modifications en cours.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagramId]);

  const toggleDay = (value: number) => {
    setWorkingDays((prev) => (prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value].sort()));
  };

  const handleImportHolidays = async () => {
    try {
      const holidays = await api.get<string[]>(`/diagrams/${diagramId}/holidays?country=${country}&year=${year}`);
      const merged = new Set([...parseDatesText(datesText, dateFormat), ...holidays.map((d) => d.slice(0, 10))]);
      setDatesText(isoListToDisplayText([...merged], dateFormat));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const handleAddPickedDate = () => {
    if (!pickedDate) return;
    // Le sélecteur natif fournit déjà une valeur ISO (AAAA-MM-JJ) — pas besoin de normalizeDateToken ici.
    const merged = new Set([...parseDatesText(datesText, dateFormat), pickedDate]);
    setDatesText(isoListToDisplayText([...merged], dateFormat));
    setPickedDate('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/diagrams/${diagramId}/calendar`, {
        workingDays,
        nonWorkingDates: parseDatesText(datesText, dateFormat),
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal card" style={{ width: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('calendar.title')}</h3>
          <button className="btn" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>

        {error && <p className="error-text">{error}</p>}

        <fieldset disabled={!canWrite} style={{ border: 'none', padding: 0, margin: 0 }}>
          <h4>{t('calendar.workingDays')}</h4>
          <div className="toolbar" style={{ marginTop: 0 }}>
            {WEEKDAYS.map((day) => (
              <label key={day.value} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="checkbox" checked={workingDays.includes(day.value)} onChange={() => toggleDay(day.value)} />
                {t(day.labelKey)}
              </label>
            ))}
          </div>

          <h4>{t('calendar.holidaysTitle')}</h4>
          <div className="toolbar" style={{ marginTop: 0 }}>
            <select value={country} onChange={(e) => setCountry(e.target.value as HolidayCountry)}>
              <option value="FR">{t('calendar.country.FR')}</option>
              <option value="BE">{t('calendar.country.BE')}</option>
            </select>
            <input
              type="number"
              style={{ width: 90 }}
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value || '0', 10))}
            />
            <button type="button" className="btn" onClick={handleImportHolidays}>
              {t('calendar.importHolidays')}
            </button>
          </div>

          <h4>{t('calendar.nonWorkingDates')}</h4>
          <p style={{ color: 'var(--color-text-muted)', margin: '4px 0' }}>
            {t('calendar.nonWorkingDatesHint', { format: dateFormat })}
          </p>
          <textarea
            rows={3}
            style={{ width: '100%' }}
            value={datesText}
            onChange={(e) => setDatesText(e.target.value)}
          />
          <div className="toolbar" style={{ marginTop: 0 }}>
            <input type="date" value={pickedDate} onChange={(e) => setPickedDate(e.target.value)} />
            <button type="button" className="btn" onClick={handleAddPickedDate}>
              {t('calendar.addDate')}
            </button>
          </div>
        </fieldset>

        <div className="toolbar" style={{ marginTop: 12 }}>
          {canWrite && (
            <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
              {t('common.confirm')}
            </button>
          )}
          <button className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
